import type { Property } from '~/core/entities/property';

import { usePropertiesListing } from '~/composables/usePropertiesListing';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

interface UseSimilarPropertiesReturn {
	properties: Ref<Property[]>;
	isLoading: Ref<boolean>;
	hasMore: Ref<boolean>;
	isEmpty: Ref<boolean>;
	error: Ref<Error | null>;
	currentPage: Ref<number>;
	totalCount: Ref<number>;
	loadSimilarProperties: () => Promise<void>;
	loadNextPage: () => Promise<void>;
	refresh: () => Promise<void>;
	reset: () => void;
}

/**
 * Composable to manage similar properties with pagination
 * Uses the dedicated getSimilarProperties endpoint
 */
export function useSimilarProperties(
	baseProperty: Ref<Property | null>
): UseSimilarPropertiesReturn {
	const { user } = useUserSession();
	const getCookieData = useCookieData();

	const similarPropertiesDataLoader = async (page: number) => {
		if (!baseProperty.value) {
			return { properties: [], hasMore: false, totalCount: 0, totalPages: 0 };
		}

		const [result, repositoryError] =
			await nuxtPropertiesRepository.getSimilarProperties(
				{
					identifier: baseProperty.value.id,
					propertyType: [baseProperty.value.propertyType?.identifier],
					region: baseProperty.value.region,
					markUserWishlistsId: user.value?.id,
					page,
					perPage: 12
				},
				getCookieData()
			);

		if (repositoryError) {
			throw new Error(repositoryError.message);
		}

		if (!result) {
			throw new Error('No result from repository');
		}

		return {
			properties: result.properties ?? [],
			hasMore:
				(result.pagination?.page ?? 1) < (result.pagination?.totalPages ?? 1),
			totalCount: result.pagination?.filteredCount ?? 0,
			totalPages: result.pagination?.totalPages ?? 0
		};
	};

	// Uses the generic composable with custom data loader
	const {
		properties,
		isLoading,
		hasMore,
		isEmpty,
		error,
		currentPage,
		totalCount,
		loadProperties,
		loadNextPage,
		refresh,
		reset: baseReset
	} = usePropertiesListing({
		dataLoader: similarPropertiesDataLoader
	});

	// Watch to reset when base property changes
	watch(
		baseProperty,
		(newProperty: Property | null, oldProperty: Property | null) => {
			if (newProperty?.id !== oldProperty?.id) {
				baseReset();
			}
		},
		{ immediate: false }
	);

	// Wrapper function to prevent loading when baseProperty is null
	const loadSimilarProperties = async () => {
		if (!baseProperty.value) {
			return;
		}
		await loadProperties();
	};

	return {
		properties,
		isLoading,
		hasMore,
		isEmpty,
		error,
		currentPage,
		totalCount,
		loadSimilarProperties,
		loadNextPage,
		refresh,
		reset: baseReset
	};
}
